import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import Layout from '../components/Layout';

interface Job {
  id: number;
  system_type: string;
  status: string;
  progress: number;
  created_at: string;
  updated_at: string;
  error_message?: string;
  settings?: Record<string, any>;
}

interface Pagination {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface ProjectInfo {
  id: number;
  name: string;
  client_name: string;
}

interface ActivitiesResponse {
  items: Job[];
  pagination: Pagination;
  project: ProjectInfo;
}

const SYSTEM_NAMES: Record<string, string> = {
  factsagent: 'FactsAgent',
  topicalmap: 'Topical Map',
  topicalmapv2_generate: 'Topical Map V2',
  topicalmapv2_pipeline: 'Topical Map V2 Pipeline',
  content: 'Content',
  content_writer: 'Content Writer',
  briefcontent: 'Brief Content',
  contentframework: 'Content Framework',
  contentframeworkv2: 'Content Framework v2',
  liveupdate: 'WP Live Update',
  payloadagent: 'Live Payload Update',
  imagegenerator: 'Image Generator',
  socialmedia: 'Social Media',
  website_audit: 'Website Audit',
};

const SYSTEM_ROUTES: Record<string, string> = {
  factsagent: '/factsagent',
  topicalmap: '/topicalmap',
  topicalmapv2_generate: '/topicalmapv2-pipeline',
  topicalmapv2_pipeline: '/topicalmapv2-pipeline',
  content: '/content',
  content_writer: '/content-writer',
  briefcontent: '/briefcontent',
  contentframework: '/contentframework',
  contentframeworkv2: '/contentframeworkv2',
  liveupdate: '/liveupdate',
  payloadagent: '/payloadagent',
  imagegenerator: '/imagegenerator',
  socialmedia: '/socialmedia',
  website_audit: '/website-audit',
};

const STATUS_STYLES: Record<string, { bg: string; text: string; badge: string }> = {
  completed: { bg: 'bg-success-100', text: 'text-success-600', badge: 'badge-success' },
  processing: { bg: 'bg-warning-100', text: 'text-warning-600', badge: 'badge-warning' },
  failed: { bg: 'bg-danger-100', text: 'text-danger-600', badge: 'badge-danger' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-500', badge: 'badge-gray' },
  pending: { bg: 'bg-gray-100', text: 'text-gray-500', badge: 'badge-gray' }
};

const getJobTitle = (job: Job): string => {
  const settings = job.settings || {};

  switch (job.system_type) {
    case 'factsagent':
      if (settings.filename) {
        return settings.filename.replace(/\.(txt|json)$/i, '');
      }
      return `Fact Sheet ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'topicalmap':
      const generators = settings.generators as string[] || [];
      if (generators.length > 0) {
        return generators.slice(0, 2).join(', ') + (generators.length > 2 ? '...' : '');
      }
      return `Topical Map ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'cppages':
      const cpGenerators = settings.generators as string[] || [];
      if (cpGenerators.length > 0) {
        return cpGenerators.slice(0, 2).join(', ') + (cpGenerators.length > 2 ? '...' : '');
      }
      return `CP Pages ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'images':
      if (settings.template) {
        return `${settings.template.charAt(0).toUpperCase() + settings.template.slice(1)} Template`;
      }
      return `Images ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'content':
      const articleTypes = settings.article_types as string[] || [];
      if (articleTypes.length > 0) {
        return `${articleTypes.length} Article Type${articleTypes.length > 1 ? 's' : ''}`;
      }
      return `Content ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'topicalmapv2_generate':
      return settings.map_name || `Topical Map V2 ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'topicalmapv2_pipeline':
      return settings.map_name ? `${settings.map_name} (Pipeline)` : `TM V2 Pipeline ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'content_writer':
      return settings.keyword || `Content Writer ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    case 'website_audit':
      if (settings.url) {
        try {
          const urlObj = new URL(settings.url);
          return `Audit: ${urlObj.hostname}`;
        } catch {
          return settings.url;
        }
      }
      return `Website Audit ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;

    default:
      return `${SYSTEM_NAMES[job.system_type] || job.system_type} ${new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }
};

export default function ProjectActivitiesPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [activities, setActivities] = useState<ActivitiesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterSystem, setFilterSystem] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [cancellingJobs, setCancellingJobs] = useState<Set<number>>(new Set());
  const [deletingJobs, setDeletingJobs] = useState<Set<number>>(new Set());

  const handleDeleteJob = async (jobId: number) => {
    if (deletingJobs.has(jobId)) return;

    if (!confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingJobs(prev => new Set(prev).add(jobId));
      await api.delete(`/jobs/${jobId}`);
      // Refresh activities list
      loadActivities();
    } catch (err: any) {
      console.error('Failed to delete job:', err);
      alert(err.response?.data?.detail || 'Failed to delete job');
    } finally {
      setDeletingJobs(prev => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  };

  const handleCancelJob = async (jobId: number) => {
    if (cancellingJobs.has(jobId)) return;

    try {
      setCancellingJobs(prev => new Set(prev).add(jobId));
      await api.post(`/jobs/${jobId}/cancel`);
      // Refresh activities list
      loadActivities();
    } catch (err: any) {
      console.error('Failed to cancel job:', err);
      alert(err.response?.data?.detail || 'Failed to cancel job');
    } finally {
      setCancellingJobs(prev => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
    }
  };

  useEffect(() => {
    loadActivities();
  }, [projectId, currentPage, filterSystem, filterStatus]);

  const loadActivities = async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '30'
      });

      if (filterSystem) params.append('system_type', filterSystem);
      if (filterStatus) params.append('status', filterStatus);

      const response = await api.get(`/projects/${projectId}/activities?${params.toString()}`);
      setActivities(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'processing':
        return <div className="spinner w-5 h-5"></div>;
      case 'failed':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (!activities?.pagination) return null;
    const { page, total_pages, has_next, has_prev, total } = activities.pagination;

    const pages: (number | string)[] = [];

    // Always show first page
    pages.push(1);

    // Show ellipsis if there's a gap
    if (page > 3) pages.push('...');

    // Show pages around current page
    for (let i = Math.max(2, page - 1); i <= Math.min(total_pages - 1, page + 1); i++) {
      if (!pages.includes(i)) pages.push(i);
    }

    // Show ellipsis if there's a gap
    if (page < total_pages - 2) pages.push('...');

    // Always show last page if more than 1 page
    if (total_pages > 1 && !pages.includes(total_pages)) pages.push(total_pages);

    return (
      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
        <div className="text-sm text-gray-500">
          Showing {((page - 1) * 30) + 1} to {Math.min(page * 30, total)} of {total} activities
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={!has_prev}
            className="btn-ghost btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>

          {pages.map((p, idx) => (
            typeof p === 'number' ? (
              <button
                key={idx}
                onClick={() => handlePageChange(p)}
                className={`btn-sm min-w-[40px] ${
                  p === page
                    ? 'btn-dark'
                    : 'btn-ghost'
                }`}
              >
                {p}
              </button>
            ) : (
              <span key={idx} className="px-2 text-gray-400">...</span>
            )
          ))}

          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={!has_next}
            className="btn-ghost btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <Link to="/" className="hover:text-gray-700">Projects</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {activities?.project && (
              <>
                <span>{activities.project.client_name}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span>{activities.project.name}</span>
              </>
            )}
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">All Activities</h1>
              <p className="text-gray-500 mt-1">
                View all jobs and activities for this project
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="btn-secondary"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-6">
          <div className="px-6 py-4 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">System:</label>
              <select
                value={filterSystem}
                onChange={(e) => {
                  setFilterSystem(e.target.value);
                  setCurrentPage(1);
                }}
                className="input-field text-sm py-2"
              >
                <option value="">All Systems</option>
                <option value="factsagent">FactsAgent</option>
                <option value="topicalmap">Topical Map</option>
                <option value="topicalmapv2_generate">Topical Map V2</option>
                <option value="topicalmapv2_pipeline">Topical Map V2 Pipeline</option>
                <option value="content">Content</option>
                <option value="content_writer">Content Writer</option>
                <option value="briefcontent">Brief Content</option>
                <option value="contentframework">Content Framework</option>
                <option value="contentframeworkv2">Content Framework v2</option>
                <option value="liveupdate">WP Live Update</option>
                <option value="payloadagent">Live Payload Update</option>
                <option value="imagegenerator">Image Generator</option>
                <option value="socialmedia">Social Media</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="input-field text-sm py-2"
              >
                <option value="">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="processing">Processing</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            {(filterSystem || filterStatus) && (
              <button
                onClick={() => {
                  setFilterSystem('');
                  setFilterStatus('');
                  setCurrentPage(1);
                }}
                className="btn-ghost btn-sm text-gray-500"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Activities List */}
        <div className="card">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner w-8 h-8"></div>
              <span className="ml-3 text-gray-500">Loading activities...</span>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <div className="text-danger-600 mb-2">{error}</div>
              <button onClick={loadActivities} className="btn-secondary btn-sm">
                Retry
              </button>
            </div>
          ) : !activities?.items.length ? (
            <div className="p-12 text-center">
              <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Activities Found</h3>
              <p className="text-gray-500">
                {filterSystem || filterStatus
                  ? 'No activities match your current filters.'
                  : 'This project has no activities yet.'}
              </p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100">
                {activities.items.map((job) => {
                  const style = STATUS_STYLES[job.status] || STATUS_STYLES.pending;
                  return (
                    <div
                      key={job.id}
                      className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${style.bg} ${style.text}`}>
                          {getStatusIcon(job.status)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {getJobTitle(job)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {SYSTEM_NAMES[job.system_type] || job.system_type}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        {/* Date */}
                        <div className="text-sm text-gray-500 text-right min-w-[140px]">
                          <div>{formatDate(job.created_at)}</div>
                        </div>

                        {/* Progress */}
                        <div className="w-32">
                          <div className="progress-bar">
                            <div
                              className={`progress-bar-fill ${
                                job.status === 'completed' ? 'bg-success-500' :
                                job.status === 'failed' ? 'bg-danger-500' : 'bg-brand-500'
                              }`}
                              style={{ width: `${job.progress}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1 text-right">{job.progress}%</p>
                        </div>

                        {/* Status Badge */}
                        <span className={`min-w-[90px] text-center ${style.badge}`}>
                          {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                        </span>

                        {/* Cancel Button - only for processing/pending jobs */}
                        {(job.status === 'processing' || job.status === 'pending') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelJob(job.id);
                            }}
                            disabled={cancellingJobs.has(job.id)}
                            className="btn-secondary btn-sm text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-50"
                            title="Cancel this job"
                          >
                            {cancellingJobs.has(job.id) ? (
                              <div className="spinner w-4 h-4"></div>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                            Cancel
                          </button>
                        )}

                        {/* Open Button - navigates to specific job */}
                        <button
                          onClick={() => navigate(`${SYSTEM_ROUTES[job.system_type] || '/'}?project_id=${projectId}&job_id=${job.id}`)}
                          className="btn-secondary btn-sm"
                          title={`Open ${SYSTEM_NAMES[job.system_type] || job.system_type}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Open
                        </button>

                        {/* Delete Button - not for processing jobs */}
                        {job.status !== 'processing' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteJob(job.id);
                            }}
                            disabled={deletingJobs.has(job.id)}
                            className="btn-ghost btn-sm text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                            title="Delete this job"
                          >
                            {deletingJobs.has(job.id) ? (
                              <div className="spinner w-4 h-4"></div>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {renderPagination()}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
